/* eslint-disable no-unused-vars */
import { useMemo, useState, useEffect } from 'react';
import { Tabs, Tab, Box, Chip, Menu, MenuItem, ListItemText, Tooltip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Iconify } from 'src/components';
import { PATH_DASHBOARD } from 'src/routes/paths';
import useIsSuperAdmin from 'src/hooks/useIsSuperAdmin';
import useIsSuperUser from 'src/hooks/useIsSuperUser';
import useAuth from 'src/hooks/useAuth';
import { getDentallyConfigs } from 'src/redux/slices/practiceIntegration';

// Reads the practice-feature-permissions slice without binding the whole
// component to a single feature — we read all permissions once and let the
// `requiresFeature` filter rule below resolve each tab's gate.
const usePermissionsMap = () => useSelector((state) => state.permissions?.permissions || {});

// Grouped nav (2026-09-30 clean-up): the previous flat strip of ~19 tabs was
// replaced with Overview + four dropdown groups (Performance, Marketing &
// Ads, Patients, Practices) + a Settings icon. Item-level visibility rules
// (feature permissions, superadmin-only, multi-practice, Dentally-connected)
// are unchanged from the flat version — only the presentation is grouped.
// A group whose items all resolve to hidden collapses entirely; a group left
// with exactly one visible item renders as a plain tab instead of a
// one-item dropdown (this is how "All Practices" behaves for single-practice
// users who still qualify via `requiresMultiplePracticesOrGroup`).
const OVERVIEW_TAB = { key: 'overview', label: 'Overview', path: PATH_DASHBOARD.analyticsReports.overview };

// Marketing Overview is a top-level tab right after Overview (not inside
// "Marketing & Ads"): it is the summary sibling of Overview for the same
// practice-manager audience, whereas the Marketing & Ads items are
// per-platform detail pages. Superuser-only while in beta, using the strict
// is_superuser check because the backing tracker API rejects everyone else.
const MARKETING_OVERVIEW_TAB = {
    key: 'marketing-overview',
    label: 'Marketing Overview',
    path: PATH_DASHBOARD.analyticsReports.marketingOverview,
    isBeta: true,
};

// Calls is a top-level tab (not inside Patients): call performance is a
// practice-manager summary like Overview, gated on the call-logs feature.
const CALLS_TAB = { key: 'calls', label: 'Calls', path: PATH_DASHBOARD.analyticsReports.calls, requiresFeature: 'call-logs' };

// Path-segment match (not substring): 'overview' must not light up for
// '/marketing-overview', nor 'marketing' for '/marketing-overview'.
const pathHasSegment = (pathname, key) => pathname.split('/').includes(key);

const GROUPS = [
    {
        groupKey: 'performance',
        label: 'Performance',
        items: [
            { key: 'by-treatment', label: 'By Treatment', path: PATH_DASHBOARD.analyticsReports.byTreatment },
            { key: 'by-practitioner', label: 'By Practitioner', path: PATH_DASHBOARD.analyticsReports.byPractitioner },
            { key: 'by-team', label: 'By Team', path: PATH_DASHBOARD.analyticsReports.byTeam },
            { key: 'by-source', label: 'By Source', path: PATH_DASHBOARD.analyticsReports.bySource },
            { key: 'trends', label: 'Trends', path: PATH_DASHBOARD.analyticsReports.trends, comingSoon: true },
        ],
    },
    {
        groupKey: 'marketing-ads',
        label: 'Marketing & Ads',
        items: [
            { key: 'google-ads', label: 'Google Ads', path: PATH_DASHBOARD.analyticsReports.googleAds, isBeta: true },
            { key: 'meta-ads', label: 'Meta Ads', path: PATH_DASHBOARD.analyticsReports.metaAds, isBeta: true },
            { key: 'tiktok-ads', label: 'TikTok', path: PATH_DASHBOARD.analyticsReports.tiktokAds, isBeta: true, requiresFeature: 'tiktok-integration' },
            { key: 'google-analytics', label: 'Google Analytics', path: PATH_DASHBOARD.analyticsReports.googleAnalytics, isBeta: true },
            { key: 'marketing', label: 'Marketing Conversion Tracker', path: PATH_DASHBOARD.analyticsReports.marketing, requiresSuperAdmin: true },
            { key: 'campaigns', label: 'Marketing Campaign Config', path: PATH_DASHBOARD.analyticsReports.marketingCampaigns, requiresSuperAdmin: true },
            { key: 'click-tracking', label: 'Click Tracking', path: PATH_DASHBOARD.analyticsReports.clickTracking, isBeta: true, requiresFeature: 'click-tracking' },
            // Strict is_superuser: the SEO rank-tracker API is IsSuperUser-only.
            { key: 'seo-rankings', label: 'SEO Rankings', path: PATH_DASHBOARD.analyticsReports.seoRankings, isBeta: true, requiresSuperUser: true },
        ],
    },
    {
        groupKey: 'patients',
        label: 'Patients',
        items: [
            { key: 'lost-leads', label: 'Lost Leads', path: PATH_DASHBOARD.analyticsReports.lostLeads },
            { key: 'whatsapp', label: 'WhatsApp', path: PATH_DASHBOARD.analyticsReports.whatsapp },
            { key: 'dentally-insights', label: 'Dentally Insights', path: PATH_DASHBOARD.analyticsReports.dentallyInsights, requiresDentally: true, requiresAdmin: true },
            { key: 'geo-locator', label: 'Geo Locator', path: PATH_DASHBOARD.analyticsReports.geoLocator, requiresFeature: 'geo-locator' },
        ],
    },
    {
        groupKey: 'practices',
        label: 'Practices',
        items: [
            // Group Dashboard is now reachable as a view-toggle inside the All
            // Practices page (icon next to the practice dropdown). The
            // /group-dashboard route is still mounted for direct-link access —
            // see src/routes/index.jsx.
            { key: 'all-practices', label: 'All Practices', path: PATH_DASHBOARD.analyticsReports.allPractices, requiresMultiplePracticesOrGroup: true },
        ],
    },
];

const badgeChipSx = (isBeta) => ({
    height: 16,
    fontSize: '9px',
    fontWeight: 600,
    bgcolor: isBeta ? '#3b82f6' : '#f59e0b',
    color: '#fff',
    '& .MuiChip-label': { px: 0.75 },
});

export default function AnalyticsReportsTabs() {
    const navigate = useNavigate();
    const location = useLocation();
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [openGroupKey, setOpenGroupKey] = useState(null);
    const { isSuperAdminUser } = useIsSuperAdmin();
    const { isSuperUser } = useIsSuperUser();

    // Get practices count from Redux
    const practices = useSelector((state) => state.practiceTreatmentList?.practiceTreatment?.results || []);
    const hasMultiplePractices = practices.length > 1;
    const isSuperAdmin = isSuperAdminUser();
    const { user } = useAuth();
    const userRole = useSelector((state) => {
        const results = state.practiceTreatmentList?.practiceTreatment?.results || [];
        const match = results.find((el) => el.user && el.user.id === user?.pk);
        return match?.role;
    });
    const isAdmin = isSuperAdmin || userRole === 'ADMIN';
    const hasGroupMembership = useSelector((state) => (state.groupDashboard?.groups?.length || 0) > 0);
    const reduxDispatch = useDispatch();
    const practiceId = useSelector((state) => state.practiceTreatmentList?.practiceId);
    const dentallyConfigs = useSelector((state) => state.practiceIntegration?.dentallyConfigs);
    const permissions = usePermissionsMap();
    // Wait for `fetchMyPermissions` to settle before deciding whether to fetch
    // Dentally configs. The shared `hasFeatureAccess` helper used inside the
    // thunk is intentionally bootstrap-safe (returns true while permissions
    // are loading), which allows legitimate first-load fetches for users who
    // DO have the feature — but also leaks 403 noise during the practice-
    // switch window when the *old* practice's permissions are still in the
    // slice. Gating this useEffect at the call site closes that window without
    // changing the global helper contract.
    const permissionsLoading = useSelector((state) => state.permissions?.loading);
    const dentallyPermLevel = permissions['dentally-integration'];

    // Load Dentally config if not already in Redux. Only fire when:
    //   1) we have a practiceId,
    //   2) permissions for the current practice have finished loading, and
    //   3) the user actually has the dentally-integration feature on this
    //      practice (anything that's not 'hidden' / undefined is fine — the
    //      backend will give the same `view`/`manage` answer).
    // This prevents PermissionDenied 403 noise in Sentry (issue
    // DENTALSEM-CRM-1BS3) for users who land on /analytics-reports without
    // dentally access.
    useEffect(() => {
        if (!practiceId) return;
        if (permissionsLoading) return;
        if (!dentallyPermLevel || dentallyPermLevel === 'hidden') return;
        if (dentallyConfigs) return;
        reduxDispatch(getDentallyConfigs(practiceId));
    }, [practiceId, dentallyConfigs, reduxDispatch, permissionsLoading, dentallyPermLevel]);

    // Handle paginated response: {results: [{is_active: true, ...}]}
    const hasDentally = Boolean(
        dentallyConfigs?.is_active ||
        dentallyConfigs?.results?.[0]?.is_active
    );

    // Shared visibility predicate — applied identically to every item across
    // every group so any future `requires*` flag we add can't silently leak
    // through one group while being honoured by another.
    const isTabVisible = useMemo(() => (tab) =>
        (!tab.requiresMultiplePractices || hasMultiplePractices) &&
        // All Practices now hosts the group-dashboard view-toggle, so it must
        // also be reachable for single-practice users who have group access.
        (!tab.requiresMultiplePracticesOrGroup || hasMultiplePractices || isAdmin || hasGroupMembership) &&
        (!tab.requiresSuperAdmin || isSuperAdmin) &&
        (!tab.requiresSuperUser || isSuperUser) &&
        (!tab.requiresDentally || hasDentally) &&
        (!tab.requiresAdmin || isAdmin) &&
        (!tab.requiresAdminOrGroup || isAdmin || hasGroupMembership) &&
        (!tab.requiresFeature || (permissions[tab.requiresFeature] && permissions[tab.requiresFeature] !== 'hidden'))
    , [hasMultiplePractices, isSuperAdmin, isSuperUser, hasDentally, isAdmin, hasGroupMembership, permissions]);

    // Resolve each group down to its visible items, then collapse:
    //   0 visible items -> group dropped entirely
    //   1 visible item  -> rendered as a plain link tab (that item's own label)
    //   2+ visible items -> rendered as a dropdown group tab
    const displayedTabs = useMemo(() => {
        const tabs = [{ type: 'link', ...OVERVIEW_TAB }];
        if (isSuperUser) tabs.push({ type: 'link', ...MARKETING_OVERVIEW_TAB });
        if (isTabVisible(CALLS_TAB)) tabs.push({ type: 'link', ...CALLS_TAB });
        GROUPS.forEach((group) => {
            const visibleItems = group.items.filter(isTabVisible);
            if (visibleItems.length === 0) return;
            if (visibleItems.length === 1) {
                tabs.push({ type: 'link', ...visibleItems[0] });
                return;
            }
            tabs.push({ type: 'group', groupKey: group.groupKey, label: group.label, items: visibleItems });
        });
        return tabs;
    }, [isTabVisible, isSuperUser]);

    const findActiveItemInGroup = (group) => group.items.find((item) => pathHasSegment(location.pathname, item.key));

    const getCurrentTabIndex = () => {
        const index = displayedTabs.findIndex((tab) => {
            if (tab.type === 'link') return pathHasSegment(location.pathname, tab.key);
            return Boolean(findActiveItemInGroup(tab));
        });
        return index >= 0 ? index : false;
    };

    const isSettingsActive = location.pathname.includes(PATH_DASHBOARD.analyticsReports.settings);

    const handleTabChange = (event, newValue) => {
        const tab = displayedTabs[newValue];
        if (!tab) return;
        // Group tabs open their menu via the Tab onClick below (MUI does not
        // fire onChange when re-clicking the already-selected tab).
        if (tab.type === 'group') return;
        navigate(tab.path);
    };

    const handleGroupItemClick = (item) => {
        setOpenGroupKey(null);
        setMenuAnchor(null);
        navigate(item.path);
    };

    const handleMenuClose = () => {
        setOpenGroupKey(null);
        setMenuAnchor(null);
    };

    const renderTabLabel = (tab) => {
        if (tab.type === 'link') {
            if (!tab.comingSoon && !tab.isBeta) return tab.label;
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {tab.label}
                    <Chip label={tab.isBeta ? 'Beta' : 'Soon'} size="small" sx={badgeChipSx(tab.isBeta)} />
                </Box>
            );
        }

        // Group tab — show "Group: Active Item" when a child route is active,
        // truncated gracefully so a long combined label never forces the tab
        // strip to overflow horizontally.
        const activeItem = findActiveItemInGroup(tab);
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: { xs: 160, sm: 220 } }}>
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {activeItem ? `${tab.label}: ${activeItem.label}` : tab.label}
                </Box>
                {activeItem?.isBeta && <Chip label="Beta" size="small" sx={badgeChipSx(true)} />}
                <Iconify icon="eva:chevron-down-fill" width={16} style={{ flexShrink: 0 }} />
            </Box>
        );
    };

    return (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tabs
                value={getCurrentTabIndex()}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                textColor="inherit"
                TabIndicatorProps={{ style: { backgroundColor: '#00AB55' } }}
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    minHeight: { xs: 36, md: 40 },
                    '& .MuiTabs-flexContainer': { gap: { xs: 0.25, sm: 0.5 } },
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: { xs: '12px', sm: '12.5px', md: '13px' },
                        minHeight: { xs: 36, md: 40 },
                        minWidth: 'auto',
                        px: { xs: 1, sm: 1.25, md: 1.75 },
                        py: 0.5,
                        color: 'rgba(145, 158, 171, 0.8)',
                        transition: 'color 0.2s',
                    },
                    '& .Mui-selected': {
                        fontWeight: 600,
                        color: '#00AB55 !important',
                    },
                }}
            >
                {displayedTabs.map((tab) => (
                    <Tab
                        key={tab.type === 'group' ? tab.groupKey : tab.key}
                        label={renderTabLabel(tab)}
                        onClick={tab.type === 'group' ? (e) => { setOpenGroupKey(tab.groupKey); setMenuAnchor(e.currentTarget); } : undefined}
                    />
                ))}
            </Tabs>

            {/* Settings — icon-only, far right of the tab strip. Lives here
                (rather than only inside the date-range bar via
                AnalyticsReportsHeader) so it's reachable directly from the
                grouped nav and reflects an active state when on the page. */}
            <Tooltip title="Report settings" arrow placement="bottom">
                <Box
                    component="button"
                    type="button"
                    onClick={() => navigate(PATH_DASHBOARD.analyticsReports.settings)}
                    aria-label="Report settings"
                    sx={{
                        flexShrink: 0,
                        width: { xs: 32, md: 36 },
                        height: { xs: 32, md: 36 },
                        mb: 0.5,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: '8px',
                        border: '1px solid transparent',
                        bgcolor: isSettingsActive ? 'rgba(0, 171, 85, 0.08)' : 'transparent',
                        color: isSettingsActive ? '#00AB55' : 'rgba(145, 158, 171, 0.8)',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                            color: '#00AB55',
                            bgcolor: 'rgba(0, 171, 85, 0.08)',
                        },
                        '&:focus-visible': {
                            outline: '2px solid #00AB55',
                            outlineOffset: 2,
                        },
                    }}
                >
                    <Iconify icon="eva:settings-2-outline" width={18} />
                </Box>
            </Tooltip>

            {displayedTabs.filter((tab) => tab.type === 'group').map((group) => (
                <Menu
                    key={group.groupKey}
                    anchorEl={openGroupKey === group.groupKey ? menuAnchor : null}
                    open={openGroupKey === group.groupKey}
                    onClose={handleMenuClose}
                    keepMounted
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                        paper: {
                            sx: {
                                mt: 0.5,
                                minWidth: 200,
                                borderRadius: '12px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                            },
                        },
                    }}
                >
                    {group.items.map((item) => {
                        const isActive = pathHasSegment(location.pathname, item.key);
                        return (
                            <MenuItem
                                key={item.key}
                                onClick={() => handleGroupItemClick(item)}
                                selected={isActive}
                                sx={{ py: 1, px: 2 }}
                            >
                                <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{ fontSize: '14px', fontWeight: isActive ? 600 : 400 }}
                                />
                                {(item.comingSoon || item.isBeta) && (
                                    <Chip
                                        label={item.isBeta ? 'Beta' : 'Soon'}
                                        size="small"
                                        sx={{ ...badgeChipSx(item.isBeta), height: 18, fontSize: '10px', ml: 1 }}
                                    />
                                )}
                            </MenuItem>
                        );
                    })}
                </Menu>
            ))}
        </Box>
    );
}
